import IndeterminateIcon from "#components/IndeterminateIcon/index.js";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useLanguage } from "contexts/language.context";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import tasksService from "services/tasks-service";

export default function TasksConsolidatedReportSettings({ data, setData }) {
  const [options, setOptions] = useState({ statuses: [] });
  const [filters, setFilters] = useState(
    data.filters || {
      branches: [],
      statuses: [],
      date: {
        enabled: false,
        from: dayjs(new Date()).subtract(3, "month").startOf("day"),
        to: dayjs(new Date()).endOf("day"),
      },
    },
  );
  const includeTaskPhotos = Boolean(data.includeTaskPhotos);
  const [loading, setLoading] = useState({ fetch: true });
  const { label } = useLanguage();

  async function getOptions() {
    const [statusesResponse] = await Promise.all([
      tasksService.getTaskStatuses(),
    ]);

    setOptions((options) => ({
      ...options,
      statuses: statusesResponse[0].ok
        ? statusesResponse[1].map((item) => {
            return {
              id: item.id,
              label: item.name,
            };
          })
        : [],
    }));

    setLoading({ ...loading, fetch: false });
  }

  useEffect(() => {
    getOptions();
  }, []);

  useEffect(() => {
    setData((current) => ({
      ...current,
      filters,
      includeTaskPhotos,
    }));
  }, [filters, includeTaskPhotos]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <FormControlLabel
          label="Include task photos"
          checked={includeTaskPhotos}
          onChange={(e) => {
            const checked = Boolean(e.target.checked);
            setData((current) => ({
              ...current,
              includeTaskPhotos: checked,
              filters,
            }));
          }}
          control={<Checkbox />}
          sx={{ m: 0, mb: -1 }}
          slotProps={{
            typography: {
              variant: "h6",
              sx: {
                fontSize: "16px !important",
              },
            },
          }}
        />
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {/* Select All */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <FormControlLabel
            label={`Branches (${data.branchGroup.branches.length}) *`}
            checked={
              filters.branches.length === data.branchGroup.branches.length &&
              data.branchGroup.branches.length > 0
            }
            onChange={(e) => {
              if (e.target.checked) {
                setFilters({
                  ...filters,
                  branches: data.branchGroup.branches.map((b) => b.id),
                });
              } else {
                setFilters({
                  ...filters,
                  branches: [],
                });
              }
            }}
            control={
              <Checkbox
                indeterminate={
                  filters.branches.length > 0 &&
                  filters.branches.length < data.branchGroup.branches.length
                }
                indeterminateIcon={<IndeterminateIcon />}
              />
            }
            sx={{ m: 0, mb: -1 }}
            slotProps={{
              typography: {
                variant: "h6",
                sx: {
                  fontSize: "16px !important",
                },
              },
            }}
          />
        </Box>

        {/* Branch List */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 1,
          }}
        >
          {data.branchGroup.branches.map((branch) => {
            return (
              <FormControlLabel
                key={"Branch: " + branch.id}
                label={branch.name}
                control={
                  <Checkbox
                    checked={filters.branches.includes(branch.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFilters({
                          ...filters,
                          branches: [...filters.branches, branch.id],
                        });
                      } else {
                        setFilters({
                          ...filters,
                          branches: filters.branches.filter(
                            (v) => v !== branch.id,
                          ),
                        });
                      }
                    }}
                    sx={{ m: 0 }}
                  />
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  m: 0,
                  mb: -2,
                }}
                slotProps={{
                  typography: {
                    sx: {
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    },
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>
      <br />
      <div />
      <br />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <FormControlLabel
            label={
              <>
                Statuses{" "}
                {loading.fetch ? (
                  <>
                    &nbsp;&nbsp;
                    <CircularProgress size={16} />
                  </>
                ) : (
                  `(${options.statuses.length})`
                )}{" "}
                *
              </>
            }
            control={
              <Checkbox
                checked={
                  options.statuses.length > 0 &&
                  filters.statuses.length === options.statuses.length
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    setFilters({
                      ...filters,
                      statuses: options.statuses.map((v) => v.id),
                    });
                  } else {
                    setFilters({
                      ...filters,
                      statuses: [],
                    });
                  }
                }}
                indeterminate={
                  filters.statuses.length > 0 &&
                  filters.statuses.length < options.statuses.length
                }
                indeterminateIcon={<IndeterminateIcon />}
              />
            }
            sx={{ m: 0, mb: -1 }}
            slotProps={{
              typography: {
                variant: "h6",
                sx: {
                  fontSize: "16px !important",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 2,
                },
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 1,
          }}
        >
          {options.statuses.map((status) => {
            return (
              <FormControlLabel
                key={"Status: " + status.id}
                checked={filters.statuses.includes(status.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFilters({
                      ...filters,
                      statuses: [...filters.statuses, status.id],
                    });
                  } else {
                    setFilters({
                      ...filters,
                      statuses: filters.statuses.filter((v) => v != status.id),
                    });
                  }
                }}
                control={<Checkbox sx={{ m: 0 }} />}
                sx={{
                  whiteSpace: "nowrap",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  m: 0,
                  mb: -2,
                }}
                slotProps={{
                  typography: {
                    sx: {
                      whiteSpace: "wrap",
                    },
                  },
                }}
                label={status.label}
              />
            );
          })}
        </Box>
      </Box>
      <br />
      <div />
      <br />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <FormControlLabel
            label={`Date range`}
            checked={filters.date.enabled}
            onChange={(e) => {
              setFilters({
                ...filters,
                date: { ...filters.date, enabled: e.target.checked },
              });
            }}
            control={<Checkbox />}
            sx={{ m: 0, mb: -1 }}
            slotProps={{
              typography: {
                variant: "h6",
                sx: {
                  fontSize: "16px !important",
                },
              },
            }}
          />
        </Box>

        <Box sx={{}}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
                pt: 1,
              }}
            >
              <DatePicker
                disabled={!filters.date.enabled}
                slotProps={{
                  textField: {
                    required: true,
                  },
                }}
                label={label("from-date-label")}
                value={filters.date.from || ""}
                onChange={(v) => {
                  setFilters({
                    ...filters,
                    date: { ...filters.date, from: v.startOf("day") },
                  });
                }}
                sx={{ flex: 1 }}
                format="DD/MM/YYYY"
              />
              <DatePicker
                disabled={!filters.date.enabled}
                slotProps={{
                  textField: {
                    required: true,
                  },
                }}
                label={label("to-date-label")}
                value={filters.date.to || ""}
                onChange={(v) => {
                  setFilters({
                    ...filters,
                    date: { ...filters.date, to: v.endOf("day") },
                  });
                }}
                sx={{ flex: 1 }}
                format="DD/MM/YYYY"
              />
            </Box>
          </LocalizationProvider>
        </Box>
      </Box>
    </Box>
  );
}
